import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import Welcome from "@/shared/components/Sections/Welcome";
import { Layer } from "@/Utils/hierarchy";

import { useFlightingROIs } from "./api/getFlightingROI";
import Breakdown from "./components/Sections/Breakdown";
import { GridContainer } from "./components/Sections/GridContainer";
import { content } from "./constants/content";
import { useAvailableFilters } from "./hooks/useAvailableFilters";
import { useFlightingStore } from "./store/useFlightingStore";

interface FlightingProps {}

export enum GeoViewToggle {
  AGGREGATE = "aggregate",
  SPLIT = "split",
}

const noDataContent = {
  title: "Insufficient Data",
  description:
    "Sorry, there is not enough data to show this module. Try selecting a difference business unit or adjusting your date range.",
};

const Flighting: React.FC<FlightingProps> = () => {
  const { startDate, endDate } = useDateRangeStore();

  const { setTargetNode, chart } = useFlightingStore();
  const { targetNode, channels, funnelStage, geographies } = useFlightingStore(
    (state) => state.filters
  );
  const { geoView } = useFlightingStore((state) => state.grid);

  const { loading: areGeosLoading, initInfo: info, nodes, loading } = useInfo();
  const hasGeoData = Boolean(info?.geographies && info?.geographies.length > 0);

  // Set default node when component mounts
  useEffect(() => {
    if (nodes && !loading && targetNode === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNode, nodes, setTargetNode]);

  const selectedNode = useMemo(
    () => (targetNode ? nodes?.getNode(targetNode) : undefined),
    [nodes, targetNode]
  );

  const areParamsValid = startDate && endDate && selectedNode;

  const { data: roiData, isLoading: isFlightingLoading } = useFlightingROIs({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    channels: channels,
    funnelStages: funnelStage,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    roiWindow: chart.ROIWindow,
    ...(hasGeoData && geographies?.length > 0 ? { geographies } : {}),
    config: {
      enabled: !!areParamsValid && !!startDate && !!endDate && !areGeosLoading && !!funnelStage,
    },
  });

  const availableFilters = useAvailableFilters();

  const { moduleOpened } = useAnalytics();
  useEffect(() => {
    moduleOpened("flighting", Layer.Portfolio);
  }, []);

  const showWelcomeScreen =
    !channels.length || !funnelStage.length || (hasGeoData ? !geographies.length : false);

  const dataView =
    !hasGeoData || geoView === GeoViewToggle.AGGREGATE ? roiData?.aggregate : roiData?.split;

  const isLoading = loading || !nodes || !targetNode || areGeosLoading;

  if (isLoading) {
    return <CircularProgress size={150} sx={{ marginY: "30rem" }} />;
  }

  const roiGraphData =
    roiData?.aggregate?.map(({ totalSpend: _totalSpend, totalRevenue: _totalRevenue, ...rest }) => {
      return {
        ...rest,
      };
    }) || [];

  const revenueGraphData =
    roiData?.aggregate?.map(({ totalRevenue, date }) => {
      return {
        date,
        totalRevenue,
      };
    }) || [];

  const totalSpendGraphData =
    roiData?.aggregate?.map(({ totalSpend, date }) => {
      return {
        date,
        totalSpend,
      };
    }) || [];

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isFlightingLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : isEmpty(dataView) ? (
        <NoData title={noDataContent.title} description={noDataContent.description} />
      ) : showWelcomeScreen ? (
        <Welcome title={content.title} />
      ) : (
        <Box marginX={4}>
          <Breakdown
            roiData={roiGraphData}
            revenueData={revenueGraphData}
            totalSpendGraphData={totalSpendGraphData}
          />
          <GridContainer title={content.title} roiData={dataView ?? []} hasGeoData={hasGeoData} />
        </Box>
      )}
    </Box>
  );
};

export default Flighting;
